import { createStyles, SimpleGrid } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useReactivateOrCreateSecodaLiteSubscription } from '@repo/api-codegen';
import { useNavigate } from 'react-router';
import { useSuspenseFeatureAccess } from '../../api';
import { GROWTH_PLAN } from '../../data/plans';
import { usePlan } from '../../hooks/usePlans';
import { PLAN_FEATURES } from './constants';
import PlansCard from './PlansCard';

const useStyles = createStyles(() => ({
	buttonRoot: {
		width: 'fit-content',
	},
}));

export default function PlansGrid() {
	const { theme } = useStyles();
	const { plan } = usePlan();
	const { secodaCoreAccess } = useSuspenseFeatureAccess();

	const navigate = useNavigate();

	const { mutateAsync: reactivateOrCreateSecodaLiteSubscription } =
		useReactivateOrCreateSecodaLiteSubscription({});

	const handleContactUsClick = (planTitle: string) => {
		window.open('https://secoda.co/upgrade', '_blank', 'noopener,noreferrer');
		showNotification({
			title: `${planTitle} plan selected`,
			message: 'A Secoda team member will reach out to you soon',
			autoClose: false,
		});
	};

	const handleSecodaAiClick = async () => {
		await reactivateOrCreateSecodaLiteSubscription({});
		navigate('/onboarding/payment');
	};

	const handleCoreClick = () => {
		handleContactUsClick('CORE');
	};

	const handlePremiumClick = () => {
		handleContactUsClick('PREMIUM');
	};

	const handleEnterpriseClick = () => {
		handleContactUsClick('ENTERPRISE');
	};

	return (
		<SimpleGrid
			cols={secodaCoreAccess ? 3 : 4}
			spacing="sm"
			maw={theme.other.width.xxxxl}
		>
			{!secodaCoreAccess && (
				<PlansCard
					title="secoda-ai"
					color="text/primary/default"
					descriptionColor="text/secondary/default"
					bgColor={theme.other.getColor('surface/primary/default')}
					description="Automate data questions and empower your team with a powerful AI assistant built for data"
					pricing="$20 per user/month"
					ctaButtonLabel="Start free trial"
					ctaOnClick={handleSecodaAiClick}
					features={PLAN_FEATURES['secoda-ai']}
					ctaButtonFilled
					gradient
				/>
			)}
			<PlansCard
				title="core"
				color="text/primary/default"
				descriptionColor="text/secondary/default"
				bgColor={theme.other.getColor('surface/primary/default')}
				description="Essential tools for modern data governance, lineage, monitoring, and AI-powered workflows"
				ctaButtonLabel="Book a demo"
				ctaOnClick={handleCoreClick}
				features={PLAN_FEATURES.core}
				ctaButtonFilled
				disabled={plan?.id === GROWTH_PLAN.id}
				includesSecodaAI
			/>
			<PlansCard
				title="premium"
				color="text/primary/default"
				descriptionColor="text/secondary/default"
				bgColor={theme.other.getColor('surface/primary/default')}
				description="For growing teams advancing data governance with automation, policies, and quality scoring"
				ctaButtonLabel="Book a demo"
				ctaOnClick={handlePremiumClick}
				features={PLAN_FEATURES.premium}
				ctaButtonFilled
				disabled={plan?.id === GROWTH_PLAN.id}
				includesSecodaAI
			/>
			<PlansCard
				title="enterprise"
				color="text/primary/default"
				descriptionColor="text/secondary/default"
				bgColor={theme.other.getColor('surface/primary/default')}
				description="For large teams handling sensitive data with strong security, access controls, and custom deployment"
				ctaButtonLabel="Book a demo"
				ctaOnClick={handleEnterpriseClick}
				features={PLAN_FEATURES.enterprise}
				ctaButtonFilled
				includesSecodaAI
			/>
		</SimpleGrid>
	);
}
